import { addDays, isWithinSeason, parsePlainDate, weekdayOf } from './dates.js';
/** Monday of the ISO week, used as the stable anchor for n-weekly plans. */
function isoWeekStart(date) {
    const weekday = weekdayOf(date);
    const offset = weekday === 0 ? -6 : 1 - weekday;
    return addDays(date, offset);
}
function weeksBetweenIsoWeeks(from, to) {
    const ms = parsePlainDate(isoWeekStart(to)).getTime() - parsePlainDate(isoWeekStart(from)).getTime();
    return Math.round(ms / (7 * 86_400_000));
}
function nthWeekdayOfMonth(date, nth, weekday) {
    const d = parsePlainDate(date);
    if (d.getUTCDay() !== weekday)
        return false;
    if (nth === -1) {
        const lastOfMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
        return lastOfMonth.getUTCDate() - d.getUTCDate() < 7;
    }
    return Math.floor((d.getUTCDate() - 1) / 7) + 1 === nth;
}
function matchesMonthlyDay(date, day) {
    const d = parsePlainDate(date);
    const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    // A plan for "the 31st" still has to fire in February, clamp to month end.
    return d.getUTCDate() === Math.min(day, lastDay);
}
export function matchesRecurrence(recurrence, date) {
    switch (recurrence.type) {
        case 'once':
            return recurrence.date === date;
        case 'weekly':
            return recurrence.weekdays.includes(weekdayOf(date));
        case 'interval_weeks': {
            if (!recurrence.weekdays.includes(weekdayOf(date)))
                return false;
            const interval = Math.max(1, recurrence.intervalWeeks);
            const delta = weeksBetweenIsoWeeks(recurrence.anchorDate, date);
            return delta >= 0 && delta % interval === 0;
        }
        case 'monthly_nth_weekday':
            return nthWeekdayOfMonth(date, recurrence.nth, recurrence.weekday);
        case 'monthly_day':
            return matchesMonthlyDay(date, recurrence.day);
    }
}
/**
 * All dates in `[from, to]` on which the plan is due.
 * The horizon is short (weeks), so a day-by-day scan is both fast enough and
 * far easier to reason about than closed-form date maths.
 */
export function expandRecurrence(plan, from, to) {
    const start = plan.startDate > from ? plan.startDate : from;
    const end = plan.endDate && plan.endDate < to ? plan.endDate : to;
    if (start > end)
        return [];
    const dates = [];
    for (let date = start; date <= end; date = addDays(date, 1)) {
        if (!isWithinSeason(date, plan.seasonStart, plan.seasonEnd))
            continue;
        if (matchesRecurrence(plan.recurrence, date))
            dates.push(date);
    }
    return dates;
}
const WEEKDAY_NAMES_DE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WEEKDAY_SHORT_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const NTH_NAMES_DE = {
    '1': 'ersten',
    '2': 'zweiten',
    '3': 'dritten',
    '4': 'vierten',
    '-1': 'letzten',
};
function listWeekdays(weekdays) {
    const sorted = [...weekdays].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b));
    return sorted.map((w) => WEEKDAY_SHORT_DE[w] ?? '?').join(' + ');
}
/** Human-readable German summary, shown next to every plan in the backoffice. */
export function describeRecurrence(recurrence) {
    switch (recurrence.type) {
        case 'once':
            return `einmalig am ${formatGermanDate(recurrence.date)}`;
        case 'weekly':
            return recurrence.weekdays.length === 7 ? 'täglich' : `wöchentlich ${listWeekdays(recurrence.weekdays)}`;
        case 'interval_weeks':
            return recurrence.intervalWeeks === 1
                ? `wöchentlich ${listWeekdays(recurrence.weekdays)}`
                : `alle ${recurrence.intervalWeeks} Wochen ${listWeekdays(recurrence.weekdays)}`;
        case 'monthly_nth_weekday':
            return `am ${NTH_NAMES_DE[String(recurrence.nth)] ?? ''} ${WEEKDAY_NAMES_DE[recurrence.weekday]} im Monat`;
        case 'monthly_day':
            return `monatlich am ${recurrence.day}.`;
    }
}
export function describeSeason(seasonStart, seasonEnd) {
    if (!seasonStart || !seasonEnd)
        return null;
    const fmt = (md) => `${Number(md.slice(3, 5))}.${Number(md.slice(0, 2))}.`;
    return `${fmt(seasonStart)} bis ${fmt(seasonEnd)}`;
}
export function formatGermanDate(date) {
    const [y, m, d] = date.split('-');
    return `${d}.${m}.${y}`;
}
export function formatGermanWeekday(date) {
    return WEEKDAY_NAMES_DE[weekdayOf(date)] ?? '';
}
//# sourceMappingURL=recurrence.js.map